import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import NetworkMap from "../components/NetworkMap";
import DealForgeLogo from "../components/DealForgeLogo";
import { 
  TrendingUp, 
  Home, 
  Hammer, 
  DollarSign, 
  Shield,
  ArrowRight,
  Check,
  Users,
  Building2,
  Target,
  Zap,
  Network,
  ChevronDown
} from "lucide-react";

// Portal configurations
const PORTAL_CONFIG = {
  investors: {
    id: "investor",
    name: "Investor Portal",
    headline: "DealForge Investor Portal",
    subheadline: "Control deal flow, connect with operators, and execute deals faster using real estate transaction infrastructure.",
    icon: TrendingUp,
    color: "#D4AF37",
    problem: {
      title: "Investors are stuck juggling fragmented tools",
      points: [
        "CRMs that don't understand real estate deals",
        "Contractor coordination through endless phone calls",
        "Lender communication scattered across email threads",
        "Spreadsheet chaos that kills deals"
      ]
    },
    features: [
      { title: "Deal Marketplace", desc: "Access off-market deals and submit your own" },
      { title: "Deal Analysis Tools", desc: "Creative finance scenarios in seconds" },
      { title: "Contractor Bid Marketplace", desc: "Verified contractors compete for your projects" },
      { title: "Lender Connections", desc: "Hard money and private lenders compete for your deals" },
      { title: "Transaction Coordination", desc: "Title, settlement, and closing in one workflow" },
      { title: "Execution Network", desc: "Connect with realtors, contractors, and lenders" }
    ]
  },
  realtors: {
    id: "realtor",
    name: "Realtor Portal",
    headline: "DealForge Realtor Portal",
    subheadline: "Connect with investor buyers, access off-market deal flow, and close transactions faster.",
    icon: Home,
    color: "#D4AF37",
    problem: {
      title: "Realtors are missing investor deal flow",
      points: [
        "Investor buyers want speed - traditional MLS doesn't cut it",
        "Off-market deals slip through the cracks",
        "No direct connection to serious cash buyers",
        "Listings sit while investors close elsewhere"
      ]
    },
    features: [
      { title: "Investor Buyer Network", desc: "Direct access to active investors" },
      { title: "Off-Market Deal Exposure", desc: "List deals before they hit MLS" },
      { title: "Direct Investor Connections", desc: "Skip the middlemen" },
      { title: "Deal Marketplace Listing", desc: "Exposure to the entire network" },
      { title: "Faster Buyer Matching", desc: "AI-powered buyer recommendations" },
      { title: "Transaction Coordination", desc: "Streamlined closing process" }
    ]
  },
  contractors: {
    id: "contractor",
    name: "Contractor Portal",
    headline: "DealForge Contractor Portal",
    subheadline: "Access investor renovation projects, submit competitive bids, and build a consistent project pipeline.",
    icon: Hammer,
    color: "#3B82F6",
    problem: {
      title: "Contractors struggle with inconsistent work",
      points: [
        "Feast or famine project pipeline",
        "Competing on price alone destroys margins",
        "No reputation system rewards quality work",
        "Chasing leads instead of doing work"
      ]
    },
    features: [
      { title: "Project Bid Marketplace", desc: "Real projects from real investors" },
      { title: "Investor Renovation Projects", desc: "Steady stream of fix-and-flip work" },
      { title: "Deal Network Exposure", desc: "Build your reputation across the platform" },
      { title: "Consistent Project Pipeline", desc: "No more feast or famine" },
      { title: "Direct Payments", desc: "Get paid through the platform" },
      { title: "Reputation System", desc: "Quality work gets rewarded" }
    ]
  },
  lenders: {
    id: "lender",
    name: "Lender Portal",
    headline: "DealForge Lender Portal",
    subheadline: "Access deal funding opportunities, connect with qualified borrowers, and deploy capital efficiently.",
    icon: DollarSign,
    color: "#10B981",
    problem: {
      title: "Lenders are hunting for quality deal flow",
      points: [
        "Chasing borrowers through brokers and middlemen",
        "Incomplete deal packages waste underwriting time",
        "No visibility into deal quality before commitment",
        "Competition based on rate alone"
      ]
    },
    features: [
      { title: "Deal Funding Marketplace", desc: "Structured deals ready for funding" },
      { title: "Investor Deal Flow", desc: "Active investors seeking capital" },
      { title: "Borrower Discovery", desc: "Pre-qualified borrowers with track records" },
      { title: "Structured Deal Submissions", desc: "Complete packages, not guesswork" },
      { title: "Direct Bidding", desc: "Compete on your terms" },
      { title: "Portfolio Management", desc: "Track all funded deals in one place" }
    ]
  },
  title: {
    id: "title",
    name: "Title Portal",
    headline: "DealForge Title Portal",
    subheadline: "Streamline closing operations, access investor deal flow, and grow your title business.",
    icon: Shield,
    color: "#10B981",
    problem: {
      title: "Title companies are order takers, not partners",
      points: [
        "Last-minute closings without preparation time",
        "No visibility into deal pipeline",
        "Competing on price with no differentiation",
        "Reactive instead of proactive closing management"
      ]
    },
    features: [
      { title: "Closing Pipeline Visibility", desc: "See deals before they hit your desk" },
      { title: "Investor Deal Coordination", desc: "Partner with investors, not just process" },
      { title: "Transaction Workflow Integration", desc: "Automated status updates and alerts" },
      { title: "Deal Network Access", desc: "Build relationships with active investors" },
      { title: "Proactive Scheduling", desc: "Plan closings before the rush" },
      { title: "Revenue Forecasting", desc: "See what's coming in your pipeline" }
    ]
  }
};

// Network Map Component
// NetworkDiagram - Uses the shared NetworkMap component
function NetworkDiagram() {
  return (
    <div className="relative w-full max-w-3xl mx-auto py-12">
      <NetworkMap variant="compact" />
      <p className="text-center text-white/50 text-sm mt-4 max-w-xl mx-auto">
        DealForge connects the entire real estate transaction ecosystem into a single execution platform.
      </p>
    </div>
  );
}

// Pricing Preview Component
function PricingPreview() {
  return (
    <div className="bg-[#0A0A0A]/80 border border-white/[0.06] p-8 max-w-md mx-auto">
      <div className="text-center mb-6">
        <span className="px-3 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-semibold uppercase">
          Founder Beta Access
        </span>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
          <span className="text-white">Primary Portal</span>
          <span className="text-[#D4AF37] font-mono font-bold text-xl">$99<span className="text-white/50 text-sm">/mo</span></span>
        </div>
        <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
          <span className="text-white">Additional Portals</span>
          <span className="text-[#D4AF37] font-mono font-bold text-xl">$99<span className="text-white/50 text-sm">/mo each</span></span>
        </div>
      </div>
      
      <div className="mt-6 p-4 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 rounded">
        <p className="text-white/70 text-sm">
          <strong className="text-[#D4AF37]">Founder pricing</strong> remains locked as long as your subscription stays active.
        </p>
      </div>
    </div>
  );
}

export default function PortalLandingPage() {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Get portal type from URL path (e.g., /investors -> investors)
  const portalType = location.pathname.replace('/', '');
  
  // Get portal config based on URL
  const portal = PORTAL_CONFIG[portalType] || PORTAL_CONFIG.investors;
  const Icon = portal.icon;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        isScrolled ? "bg-black/90 backdrop-blur-md border-b border-white/[0.06]" : "bg-transparent"
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <span className="text-[17px] font-bold text-white tracking-tight">
                DEAL<span className="text-[#D4AF37]">FORGE</span><sup className="text-[#D4AF37] text-xs">™</sup>
              </span>
            </Link>
            
            {/* Portal Dropdown */}
            <div className="hidden md:flex items-center gap-6">
              <div className="relative group">
                <button className="flex items-center gap-1 text-white/70 hover:text-white transition-colors">
                  Portals <ChevronDown className="w-4 h-4" />
                </button>
                <div className="absolute top-full left-0 mt-2 w-48 bg-[#0A0A0A] border border-white/[0.08] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <Link to="/investors" className="flex items-center gap-2 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/[0.02] transition-colors">
                    <TrendingUp className="w-4 h-4" /> Investor Portal
                  </Link>
                  <Link to="/realtors" className="flex items-center gap-2 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/[0.02] transition-colors">
                    <Home className="w-4 h-4" /> Realtor Portal
                  </Link>
                  <Link to="/contractors" className="flex items-center gap-2 px-4 py-3 text-white/70 hover:text-blue-400 hover:bg-white/[0.02] transition-colors">
                    <Hammer className="w-4 h-4" /> Contractor Portal
                  </Link>
                  <Link to="/lenders" className="flex items-center gap-2 px-4 py-3 text-white/70 hover:text-emerald-400 hover:bg-white/[0.02] transition-colors">
                    <DollarSign className="w-4 h-4" /> Lender Portal
                  </Link>
                  <Link to="/title" className="flex items-center gap-2 px-4 py-3 text-white/70 hover:text-emerald-400 hover:bg-white/[0.02] transition-colors">
                    <Shield className="w-4 h-4" /> Title Portal
                  </Link>
                </div>
              </div>
              <Link to="/launch" className="text-white/70 hover:text-white transition-colors">
                Platform
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-white/70 hover:text-white">
                  Sign In
                </Button>
              </Link>
              <Link to="/beta-application">
                <Button className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold">
                  Apply for Beta
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center`} 
                 style={{ backgroundColor: `${portal.color}15`, border: `1px solid ${portal.color}40` }}>
              <Icon className="w-10 h-10" style={{ color: portal.color }} />
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            {portal.headline.split(' ').slice(0, -1).join(' ')} <span style={{ color: portal.color }}>{portal.headline.split(' ').slice(-1)}</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-8">
            {portal.subheadline}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/beta-application">
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold px-8 py-6 text-lg">
                Apply for Beta Access <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/launch">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/[0.04] px-8 py-6 text-lg">
                Join Founder Network
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 px-6 bg-[#0A0A0A]/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            {portal.problem.title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {portal.problem.points.map((point, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-red-500/[0.05] border border-red-500/20 rounded-lg">
                <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-400 text-sm">×</span>
                </div>
                <p className="text-white/80">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            DealForge is the <span className="text-[#D4AF37]">Solution</span>
          </h2>
          <p className="text-white/60 text-lg mb-12">
            DealForge connects the entire deal execution chain: Investors, Realtors, Contractors, Lenders, and Title companies. 
            All operating inside one transaction platform.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 bg-[#0A0A0A]/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
            {portal.name} <span style={{ color: portal.color }}>Features</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portal.features.map((feature, idx) => (
              <div key={idx} className="p-6 bg-[#0A0A0A]/80 border border-white/[0.06] hover:border-white/[0.12] transition-all">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="w-5 h-5" style={{ color: portal.color }} />
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                </div>
                <p className="text-white/60 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Network Effect Section */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
            The DealForge <span className="text-[#D4AF37]">Execution Network</span>
          </h2>
          <p className="text-white/60 text-center max-w-2xl mx-auto mb-8">
            DealForge is not just software. It is a real estate execution network. 
            Every deal requires multiple participants—DealForge connects them inside a single system.
          </p>
          
          <NetworkDiagram />
        </div>
      </section>

      {/* Founder Beta Section */}
      <section className="py-16 px-6 bg-[#0A0A0A]/50">
        <div className="max-w-4xl mx-auto text-center">
          <span className="px-4 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm font-semibold rounded-full">
            Limited Beta Access
          </span>
          
          <h2 className="text-2xl md:text-3xl font-bold mt-6 mb-4">
            Join the <span className="text-[#D4AF37]">Founder Beta</span>
          </h2>
          
          <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
            DealForge is currently accepting a limited number of beta users. 
            Approved users receive early platform access, founder pricing locked in, 
            influence on platform development, and priority feature access.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl mx-auto mb-8">
            <div className="flex items-center gap-3 p-4 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 rounded-lg">
              <Check className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-white/80">Early platform access</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 rounded-lg">
              <Check className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-white/80">Founder pricing forever</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 rounded-lg">
              <Check className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-white/80">Shape platform development</span>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 rounded-lg">
              <Check className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-white/80">Priority feature access</span>
            </div>
          </div>
          
          <p className="text-white/50 text-sm">
            Beta access is application-based and manually approved.
          </p>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
            Founder <span className="text-[#D4AF37]">Pricing</span>
          </h2>
          
          <PricingPreview />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0A0A0A]/50 to-black">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Apply for <span className="text-[#D4AF37]">Beta Access</span>
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Join the DealForge execution network.
          </p>
          
          <Link to="/beta-application">
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold px-12 py-6 text-lg">
              Apply for Beta <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <span className="text-[15px] font-bold text-white tracking-tight">
              DEAL<span className="text-[#D4AF37]">FORGE</span>
            </span>
          </div>
          <p className="text-white/40 text-sm">
            © 2025 DealForge. Real Estate Transaction Infrastructure.
          </p>
        </div>
      </footer>
    </div>
  );
}
